import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import "./HistoryPage.css";
import BASE_URL from "../../../../config/apiConfig";

const HistoryPage = () => {
  const { transactionId } = useParams();
  const location = useLocation();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { cropname } = location.state || {};

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/user/crop-clinic/history/${transactionId}`,
        );

        setHistory(response.data.history || []);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch history. Please try again later.");
        setLoading(false);
      }
    };

    fetchHistory();
  }, [transactionId]);

  const formatDate = (date) => {
    if (!date) return "Date unavailable";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "Date unavailable";
    }

    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="farmology-history-page">
      <section className="history-page-hero">
        <div className="history-hero-overlay" />
        <div className="history-hero-grid" />

        <div className="history-hero-content">
          <span className="history-hero-eyebrow">FARMOLOGY PLATFORM</span>

          <div className="history-hero-title-row">
            <div className="history-hero-copy">
              <h1>Crop Clinic History</h1>
              <p>
                Review your previous crop diagnoses, uploaded crop images and
                recommended precautions in one place.
              </p>
            </div>

            <div className="history-hero-meta">
              <span>CROP SERVICE</span>
              <strong>{cropname || "Crop Clinic"}</strong>
            </div>
          </div>
        </div>

        <div className="history-hero-bottom">
          <span>CLINIC HISTORY</span>
          <span>DIAGNOSIS · PRECAUTIONS · DATE</span>
        </div>
      </section>

      <main className="history-page-content">
        <section className="history-intro">
          <div className="history-intro-copy">
            <span className="history-eyebrow">YOUR CROP RECORD</span>
            <h2>Previous crop consultations</h2>
            <p>
              Keep track of what was identified during earlier Crop Clinic
              consultations and use those insights when reviewing crop health.
            </p>
          </div>

          <div className="history-count-card">
            <span>RECORDS</span>
            <strong>{history.length}</strong>
          </div>
        </section>

        {loading ? (
          <div className="history-state-card">
            <div className="history-spinner" />
            <strong>Loading your clinic history</strong>
            <span>Please wait while we retrieve your previous records.</span>
          </div>
        ) : error ? (
          <div className="history-state-card history-error-state">
            <div className="history-state-icon">!</div>
            <strong>Unable to load history</strong>
            <span>{error}</span>
          </div>
        ) : history.length === 0 ? (
          <div className="history-state-card">
            <div className="history-state-icon">+</div>
            <strong>No clinic history yet</strong>
            <span>No history found for this subscription.</span>
          </div>
        ) : (
          <section className="history-list">
            {history.map((entry, index) => (
              <article className="history-card" key={index}>
                <div className="history-card-index">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>

                <div className="history-image">
                  {entry.image_name ? (
                    <img
                      src={`${BASE_URL}/media/crop_images/${entry.image_name}`}
                      alt={`Crop ${entry.crop_name || cropname || "crop"}`}
                    />
                  ) : (
                    <div className="history-placeholder">
                      <span>NO IMAGE</span>
                      <strong>Image unavailable</strong>
                    </div>
                  )}
                </div>

                <div className="history-details">
                  <div className="history-card-top">
                    <span className="history-card-kicker">CROP DIAGNOSIS</span>
                    <span className="history-date-badge">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>

                  <h3>{entry.crop_name || cropname || "Crop"}</h3>

                  <div className="history-info-grid">
                    <div className="history-info-block">
                      <span>DIAGNOSIS</span>
                      <p>{entry.diagnosis || "No diagnosis available."}</p>
                    </div>

                    <div className="history-info-block">
                      <span>PRECAUTIONS</span>
                      <p>{entry.precaution || "No precautions available."}</p>
                    </div>
                  </div>

                  <div className="history-card-footer">
                    <span>ANALYZED ON</span>
                    <strong>{formatDate(entry.created_at)}</strong>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
